import * as React from 'react'

import {
  createTokenRegex,
  opensAsFact,
} from '@/components/milkdown/jinja/tokenRegex'
import { cn } from '@/lib/utils'

interface JinjaSegment {
  text: string
  /** True for a Jinja/Fact token (`{{ x }}`, `{% s %}`, `{# c #}`, `{fact}`). */
  isToken: boolean
  /** Offset in the source string — a stable, data-dependent React key. */
  start: number
}

/**
 * Split `text` into plain and token segments using the SAME regex the Milkdown
 * jinja node uses, so a read-only surface tokenizes identically to the editor.
 *
 * The shared regex is global (stateful), so a fresh one is created per call.
 */
function splitJinjaSegments(text: string): JinjaSegment[] {
  const tokenRe = createTokenRegex()
  const segments: JinjaSegment[] = []
  let lastIndex = 0
  let match: RegExpExecArray | null

  while ((match = tokenRe.exec(text)) !== null) {
    if (match.index > lastIndex) {
      segments.push({
        text: text.slice(lastIndex, match.index),
        isToken: false,
        start: lastIndex,
      })
    }
    segments.push({ text: match[0], isToken: true, start: match.index })
    lastIndex = match.index + match[0].length
  }
  if (lastIndex < text.length) {
    segments.push({
      text: text.slice(lastIndex),
      isToken: false,
      start: lastIndex,
    })
  }
  return segments
}

function jinjaTokenClassName(value: string): string {
  return `jinja-token ${
    opensAsFact(value) ? 'jinja-token--fact' : 'jinja-token--jinja'
  }`
}

/**
 * Read-only templated text with the duty scripts' token colors.
 *
 * Reuses the editor's token regex AND its `.jinja-token--fact` /
 * `--jinja` classes (colors live in index.css), so `{{ patient_age }}` reads
 * pink and `{visit_type}` blue exactly as in a script card — without mounting a
 * Milkdown editor, which would be both heavy and editable. Because both sides
 * key off the same regex and CSS, the two surfaces cannot drift.
 */
export function JinjaText({
  text,
  className,
}: {
  text: string
  className?: string
}) {
  const segments = splitJinjaSegments(text)

  return (
    <span className={cn('whitespace-pre-wrap', className)}>
      {segments.map((segment) =>
        segment.isToken ? (
          <span
            key={segment.start}
            className={jinjaTokenClassName(segment.text)}>
            {segment.text}
          </span>
        ) : (
          <React.Fragment key={segment.start}>{segment.text}</React.Fragment>
        )
      )}
    </span>
  )
}
